import React from 'react';
import { Search, Star, Play } from 'lucide-react';

/**
 * Surah picker card — search input, selected-surah summary, and the
 * scrollable list of all matching surahs. Pure presentational; parent
 * owns all state and handlers.
 */
export const SurahPicker = ({
  isKidsMode,
  searchQuery,
  setSearchQuery,
  filteredSurahs,
  selectedSurah,
  favorites,
  selectSurah,
  toggleFavorite,
  startPractice,
  getSelectedSurahInfo,
}) => (
  <div
    className={`p-6 ${
      isKidsMode
        ? 'bg-white rounded-3xl shadow-[0_8px_0_0_rgba(0,0,0,0.1)]'
        : 'bg-white/70 backdrop-blur-sm rounded-xl border border-stone-200'
    }`}
    data-testid="surah-picker"
  >
    <h2
      className={`text-2xl font-bold mb-6 ${
        isKidsMode ? 'font-kids' : 'font-elegant'
      }`}
    >
      Sure Seç
    </h2>

    <div className="mb-4">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Sure adı veya numara ile ara... (örn: Yasin, Fatiha, 36)"
          className={`w-full pl-11 pr-4 py-3 border rounded-lg focus:outline-none focus:ring-2 ${
            isKidsMode
              ? 'border-slate-200 focus:ring-green-400 rounded-2xl'
              : 'border-stone-300 focus:ring-teal-500'
          }`}
          data-testid="surah-search-input"
        />
      </div>
      {searchQuery && (
        <p className="text-sm text-gray-500 mt-2">
          {filteredSurahs.length} sure bulundu
        </p>
      )}
    </div>

    {selectedSurah && (
      <div
        className={`mb-6 p-4 rounded-lg ${
          isKidsMode
            ? 'bg-green-50 rounded-2xl border-2 border-green-200'
            : 'bg-emerald-50 border border-emerald-200'
        }`}
      >
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <div className="text-xs text-gray-500 mb-1">Seçilen Sure:</div>
            <div className="font-bold text-lg">
              {getSelectedSurahInfo()?.id}. {getSelectedSurahInfo()?.transliteration}
            </div>
            <div className="text-sm text-gray-600">
              {getSelectedSurahInfo()?.translation} • {getSelectedSurahInfo()?.total_verses} ayet
            </div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => toggleFavorite(selectedSurah)}
              className={`p-3 rounded-lg transition-all ${
                favorites.includes(selectedSurah)
                  ? 'text-yellow-500'
                  : 'text-gray-400 hover:text-yellow-500'
              }`}
              data-testid="favorite-toggle-button"
            >
              <Star className={`w-6 h-6 ${favorites.includes(selectedSurah) ? 'fill-yellow-500' : ''}`} />
            </button>
            <button
              onClick={startPractice}
              className={`px-6 py-3 font-bold text-white transition-all flex items-center gap-2 ${
                isKidsMode
                  ? 'bg-green-400 rounded-full shadow-[0_4px_0_0_rgba(0,0,0,0.2)] hover:shadow-none hover:translate-y-1'
                  : 'bg-emerald-800 rounded-md hover:bg-emerald-900'
              }`}
              data-testid="start-practice-button"
            >
              <Play className="w-5 h-5" />
              Başla
            </button>
          </div>
        </div>
      </div>
    )}

    <div className="max-h-96 overflow-y-auto space-y-2">
      {filteredSurahs.length > 0 ? (
        filteredSurahs.map((surah) => (
          <button
            key={surah.id}
            onClick={() => selectSurah(surah)}
            className={`w-full p-4 text-left rounded-lg border-2 transition-all ${
              selectedSurah === surah.id
                ? isKidsMode
                  ? 'border-green-400 bg-green-50'
                  : 'border-emerald-700 bg-emerald-50'
                : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
            } ${isKidsMode ? 'rounded-2xl' : ''}`}
            data-testid={`surah-item-${surah.id}`}
          >
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <div className="font-bold">
                  {surah.id}. {surah.transliteration}
                </div>
                <div className="text-sm text-gray-500">
                  {surah.translation} • {surah.total_verses} ayet
                </div>
              </div>
              <div className="flex items-center gap-3">
                {favorites.includes(surah.id) && (
                  <Star className="w-5 h-5 text-yellow-500 fill-yellow-500" />
                )}
                <div className="font-arabic text-xl">{surah.name}</div>
              </div>
            </div>
          </button>
        ))
      ) : (
        <p className="text-center text-gray-500 py-8">Sure bulunamadı</p>
      )}
    </div>
  </div>
);

export default SurahPicker;
